import { useParams, Link } from 'react-router-dom'
import useSWR from 'swr'
import { useState } from 'react'
import { api, fetcher } from '../api/client'
import AIToolsPanel from '../components/AIToolsPanel'
import SendEmailModal from '../components/SendEmailModal'

export default function ProspectDetailPage() {
    const { id } = useParams()
    const { data: p, mutate } = useSWR<any>(id ? `/api/prospects/${id}` : null, fetcher)
    const [showAI, setShowAI] = useState(false)
    const [showEmail, setShowEmail] = useState<any | null>(null)

    if (!p) return <p className="muted">Loading prospect...</p>

    return (
        <div>
            <Link to="/prospects">← Back to prospects</Link>
            <h1>{p.name}</h1>
            <p className="muted">{p.title ?? '(no title)'} — {p.company} {p.industry && <>· {p.industry}</>}</p>

            <div className="split">
                <div className="card">
                    <h2>Lead</h2>
                    {p.lead ? <>
                        <p>Score: <strong className="score">{p.lead.score}</strong> · Status: <strong>{p.lead.status}</strong></p>
                        <p className="muted">{p.lead.rankingReason ?? '(no reason recorded)'}</p>
                        <ScoreBreakdown json={p.lead.scoreBreakdownJson} />
                        <button onClick={() => rankNow(p.id, mutate)} className="secondary">Re-rank</button>
                    </> : <>
                        <p className="muted">Not yet ranked.</p>
                        <button onClick={() => rankNow(p.id, mutate)}>Rank Now</button>
                    </>}
                </div>

                <div className="card">
                    <h2>Contacts</h2>
                    {p.contacts?.length === 0 ? <p className="muted">No contacts yet.</p> :
                        <ul style={{ paddingLeft: 16, margin: 0 }}>
                            {p.contacts?.map((c: any) => (
                                <li key={c.id}><strong>{c.contactType}:</strong> {c.value}
                                    {c.contactType === 'Email' && <button className="secondary" style={{ marginLeft: 8, padding: '2px 8px', fontSize: 12 }}
                                        onClick={() => setShowEmail({ contactId: c.id, prospectId: p.id, to: c.value })}>Send</button>}
                                </li>
                            ))}
                        </ul>}
                </div>
            </div>

            <div className="card">
                <div className="row">
                    <h2 style={{ margin: 0 }}>AI Tools</h2>
                    <div className="grow"></div>
                    <button onClick={() => setShowAI(s => !s)} data-testid="toggle-ai-tools">
                        {showAI ? 'Hide' : 'Open'} AI Tools
                    </button>
                </div>
                {showAI && <AIToolsPanel prospect={p} onSendEmail={(draft) => setShowEmail({
                    prospectId: p.id, to: p.email ?? '',
                    subject: draft.subject, body: draft.body
                })} />}
            </div>

            <SearchEnrichmentPanel prospectId={p.id} enrichmentJson={p.enrichmentDataJson} onRefresh={mutate} />

            {p.notes && <div className="card"><h2>Notes</h2><p>{p.notes}</p></div>}

            {showEmail && <SendEmailModal initial={showEmail} onClose={() => setShowEmail(null)} />}
        </div>
    )
}

function ScoreBreakdown({ json }: { json?: string }) {
    if (!json) return null
    let parsed: any
    try { parsed = JSON.parse(json) } catch { return null }
    const items = Object.entries(parsed)
    if (items.length === 0) return null
    return (
        <div className="row" style={{ gap: 16, marginTop: 8 }}>
            {items.map(([k, v]) => (
                <span key={k} className="muted"><strong>{cap(k)}</strong> {v as number}</span>
            ))}
        </div>
    )
}

function cap(s: string) { return s.charAt(0).toUpperCase() + s.slice(1) }

async function rankNow(prospectId: string, refresh: () => void) {
    await api('/api/leads/rank', { method: 'POST', body: JSON.stringify({ prospectIds: [prospectId] }) })
    /* poll for a few seconds — ranking job runs in background */
    for (let i = 0; i < 10; i++) {
        await new Promise(r => setTimeout(r, 1500))
        await refresh()
    }
}

/* Surfaces search-driven enrichment: recent news + people discovered from the open web. */
function SearchEnrichmentPanel({ prospectId, enrichmentJson, onRefresh }:
    { prospectId: string, enrichmentJson?: string, onRefresh: () => void }) {

    const [busy, setBusy] = useState(false)
    const [msg, setMsg] = useState<string | null>(null)

    let news: any[] = []
    let people: any[] = []
    let extraSignals: string[] = []
    let lastReEnrichedAt: string | null = null
    try {
        const parsed = enrichmentJson ? JSON.parse(enrichmentJson) : {}
        news = parsed.recent_news ?? []
        people = parsed.discovered_people ?? []
        extraSignals = parsed.extra_signals ?? []
        lastReEnrichedAt = parsed.last_re_enriched_at ?? null
    } catch { /* ignore */ }

    async function reEnrich() {
        setBusy(true); setMsg(null)
        try {
            await api(`/api/prospects/${prospectId}/re-enrich`, { method: 'POST' })
            setMsg('Re-enrichment queued — refresh in 30-60s for results.')
            /* poll */
            for (let i = 0; i < 30; i++) {
                await new Promise(r => setTimeout(r, 2000))
                await onRefresh()
            }
        } catch (e: any) { setMsg(`Error: ${e.message}`) }
        finally { setBusy(false) }
    }

    const hasAny = news.length > 0 || people.length > 0 || extraSignals.length > 0

    return (
        <div className="card">
            <div className="row">
                <h2 style={{ margin: 0 }}>Web Intel</h2>
                <div className="grow"></div>
                {lastReEnrichedAt && <span className="muted">Last refreshed {new Date(lastReEnrichedAt).toLocaleString()}</span>}
                <button onClick={reEnrich} disabled={busy} data-testid="re-enrich">
                    {busy ? 'Searching the web...' : (hasAny ? 'Re-enrich' : 'Run Web Search')}
                </button>
            </div>
            {msg && <div className={msg.startsWith('Error') ? 'error' : 'success'} style={{ marginTop: 8 }}>{msg}</div>}

            {!hasAny && !busy && <p className="muted">No web intel yet. Click "Run Web Search" to find recent news + decision makers from outside the company website.</p>}

            {people.length > 0 && (
                <div style={{ marginTop: 12 }}>
                    <strong>Decision Makers Found ({people.length})</strong>
                    <ul style={{ paddingLeft: 16 }}>
                        {people.map((p, i) => (
                            <li key={i} style={{ marginBottom: 6 }}>
                                <strong>{p.name}</strong>
                                {p.title && <span className="muted"> — {p.title}</span>}
                                {p.linkedin_url && <> · <a href={p.linkedin_url} target="_blank" rel="noreferrer">LinkedIn</a></>}
                                {p.email && <> · {p.email}</>}
                                {p.context && <div className="muted" style={{ fontSize: 12 }}>{p.context}</div>}
                                {p.source_url && <div className="muted" style={{ fontSize: 11 }}><a href={p.source_url} target="_blank" rel="noreferrer">source</a></div>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {news.length > 0 && (
                <div style={{ marginTop: 12 }}>
                    <strong>Recent News ({news.length})</strong>
                    <ul style={{ paddingLeft: 16 }}>
                        {news.map((n, i) => (
                            <li key={i} style={{ marginBottom: 6 }}>
                                <a href={n.url} target="_blank" rel="noreferrer">{n.title}</a>
                                {n.date && <span className="muted"> · {n.date}</span>}
                                {n.summary && <div className="muted" style={{ fontSize: 12 }}>{n.summary}</div>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {extraSignals.length > 0 && (
                <div style={{ marginTop: 12 }}>
                    <strong>Additional Signals ({extraSignals.length})</strong>
                    <ul style={{ paddingLeft: 16 }}>
                        {extraSignals.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                </div>
            )}
        </div>
    )
}
